import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import CrudForm from "../../components/dashboard/form/CrudForm";
import { useCrud } from "../../hooks/useCrud";
import Swal from "sweetalert2";
import authService from "../../services/authService";
const generateSlug = (text) =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

export default function ChildCategory() {
  const navigate = useNavigate();
const [searchText, setSearchText] = useState("");
  const {
    data: childCategories = [],
    loading: childLoading,
    errors: childErrors,
    createItem,
    updateItem,
    deleteItem,
  } = useCrud(`${process.env.REACT_APP_API_URL}/backend/child_categories`);

  const {
    data: subCategories = [], 
    loading: subLoading,
    errors: subErrors,
  } = useCrud(`${process.env.REACT_APP_API_URL}/backend/sub-categories`);

  const {
    data: categories = [],
    loading: catLoading,
    errors: catErrors,
  } = useCrud(`${process.env.REACT_APP_API_URL}/backend/categories`);

  const [editingItem, setEditingItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const allowedFields = useMemo(
    () => [
      "category_id",
      "sub_category_id",
      "name",

      "status",
      "seo_title",
      "seo_description",
      "seo_keywords",
      "description",
    ],
    []
  );

  const getFormData = useCallback(
    (item) => {
      if (!item) {
        return {
          category_id: "",
          sub_category_id: "",
          name: "",

          status: 1,
          seo_title: "",
          seo_description: "",
          seo_keywords: "",
          description: "",
        };
      }
      return allowedFields.reduce((acc, field) => {
        acc[field] = field === "status" ? Number(item[field] ?? 1) : item[field] || "";
        return acc;
      }, {});
    },
    [allowedFields]
  );

  const hasDuplicate = useCallback(
    (name, slug, id = null) =>
      childCategories.some(
        (item) =>
          (item.name.toLowerCase() === name.toLowerCase() ||
            item.slug.toLowerCase() === slug.toLowerCase()) &&
          item.id !== id
      ),
    [childCategories]
  );

  const showSwal = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
       confirmButtonColor: "#214b7a", 
    });
  }, []);
const createChildCategory = async (data) => {
  const formData = new FormData();

  formData.append("category_id", String(data.category_id));
  formData.append("sub_category_id", String(data.sub_category_id));
  formData.append("name", data.name.trim());
  formData.append("slug", data.slug || generateSlug(data.name));
  formData.append("status", String(data.status ?? 1));
  formData.append("seo_title", data.seo_title || "");
  formData.append("seo_description", data.seo_description || "");
  formData.append("seo_keywords", data.seo_keywords || "");
  formData.append("description", data.description || "");

  const token = authService.getToken?.();

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/backend/child_categories`,
    {
      method: "POST",
      headers: token
        ? { Authorization: `Bearer ${token}` }
        : {},
      body: formData,
    }
  );

  const result = await response.json();

  if (!response.ok) {
    console.error("Create child category API error:", result);
    throw new Error(
      result.detail
        ?.map((item) => `${item.loc?.at(-1)}: ${item.msg}`)
        .join(", ") || "Failed to create child category."
    );
  }

  console.log("Child category created:", result);
  return result;
};
  const handleSubmit = useCallback(
    async (formData) => {
      if (!formData.name.trim()) {
        showSwal("Error!", "Child category name is required.", "warning");
        return;
      }
      if (!formData.category_id) {
        showSwal("Error!", "Category must be selected.", "warning");
        return;
      }
      if (!formData.sub_category_id) {
        showSwal("Error!", "Sub-category must be selected.", "warning");
        return;
      }

      formData.slug = generateSlug(formData.name);

      if (hasDuplicate(formData.name, formData.slug, editingItem?.id)) {
        showSwal(
          "Duplicate Found!",
          "A child category with the same name or slug already exists.",
          "warning"
        );
        return;
      }

      const dataToSend = {};
      allowedFields.forEach((field) => {
        dataToSend[field] = formData[field];
      });
      dataToSend.slug = formData.slug;

      try {
       if (editingItem) {
  await updateItem(editingItem.id, dataToSend);
  showSwal("Updated!", "The child category has been updated.", "success");
} else {
  await createItem(dataToSend);
  showSwal("Created!", "A new child category has been created.", "success");
}
        setModalOpen(false);
        setEditingItem(null);
      } catch (error) {
        showSwal("Error!", error.message || "Something went wrong.", "error");
      }
    },
    [allowedFields, editingItem, updateItem, createItem, hasDuplicate, showSwal]
  );

  const openModal = useCallback(
    (item = null) => {
      if (!categories.length && !catLoading) {
        showSwal("Error!", "No categories available. Please create a category first.", "warning");
        return;
      }
      if (!subCategories.length && !subLoading) {
        showSwal("Error!", "No sub-categories available. Please create a sub-category first.", "warning");
        return;
      }
      setEditingItem(item);
      setModalOpen(true);
    },
    [categories, catLoading, subCategories, subLoading, showSwal]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
  }, []);

const handleDelete = useCallback(
  (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#309bdd", // 🔵 custom blue
      cancelButtonColor: "#6c757d",  // 🔴 cancel red (optional)
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteItem(id);
          showSwal("Deleted!", "The child category has been deleted.", "success");
        } catch (error) {
          showSwal("Error!", error.message || "Delete failed.", "error");
        }
      }
    });
  },
  [deleteItem, showSwal]
);

  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        const updatedData = {
          category_id: item.category_id,
          sub_category_id: item.sub_category_id,
          name: item.name,
          slug: item.slug,
          status: item.status ? 0 : 1,
        };
        await updateItem(item.id, updatedData);
        showSwal("Success!", "Child category status updated.", "success");
      } catch {
        showSwal("Error!", "Failed to update status.", "error");
      }
    },
    [updateItem, showSwal]
  );

  const tableData = useMemo(
    () =>
      childCategories.map((item, idx) => {
        const cat = categories.find((c) => c.id === item.category_id);
        const sub = subCategories.find((s) => s.id === item.sub_category_id);
        return {
          ...item,
          sl: idx + 1,
          category_name: cat ? cat.name : "—",
          sub_category_name: sub ? sub.name : "—",
        };
      }),
    [childCategories, categories, subCategories]
  );

  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button className="btn btn-secondary d-flex align-items-center" onClick={() => navigate(-1)}>
            <span>Back</span>
          </button>
          <button type="button" className="btn btn-primary"  
                   style={{
    backgroundColor: "#214b7a",
    borderColor: "#214b7a",
    color: "#fff"
  }}
          onClick={() => openModal()}>
            Create Child Category
          </button>
        </div>

        <div className="card shadow">
                       <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
       <h4 className="mb-0">Child Categories</h4>

  <input
    type="text"
    className="form-control"
    placeholder="Search products..."
    value={searchText}
    onChange={(e) => setSearchText(e.target.value)}
    style={{
      width: "260px",
      maxWidth: "100%",
      borderRadius: "6px",
    }}
  />
</div>

          {modalOpen && (
            <div
              className="modal fade show"
              tabIndex={-1}
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
              aria-modal="true"
              role="dialog"
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header d-flex justify-content-between align-items-center">
                    <h5 className="modal-title">
                      {editingItem ? "Edit Child Category" : "Create Child Category"}
                    </h5>
                    <button type="button" className="btn-close .btn-danger" aria-label="Close" onClick={closeModal} />
                  </div>
                  <div className="modal-body">
                    {catLoading || subLoading ? (
                      <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <CrudForm
                        initialData={getFormData(editingItem)}
                        onSubmit={handleSubmit}
                        allowedFields={allowedFields}
                        categories={categories}
                        subCategories={subCategories} 
                        
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {childLoading && !tableData.length ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="card-body">
              <CrudTable
                data={tableData}
                onEdit={openModal}
                onDelete={handleDelete}
                onToggleStatus={handleToggleStatus}
                 searchText={searchText}
                columns={["sl", "id", "category_id", "sub_category_id", "name", "category_name", "sub_category_name", "status"]}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardWrapper>
  );
}